import { useState, useMemo } from "react";
import { useSubscription } from "../../context/SubscriptionContext";
// Environment variables loaded from .env
import { AlertTriangle, XCircle, CreditCard, X, ArrowUpRight, RefreshCw } from "lucide-react";
import { Link, useLocation } from "wouter";

/**
 * BillingAlertBanner — Global notification banners rendered at the top of
 * the dashboard. Automatically determines which alerts to show based on
 * the current subscription state.
 *
 * States handled:
 *   1. Subscription expiring (≤ 7 days) — amber warning
 *   2. Subscription expired — red destructive
 *   3. Payment failure — red destructive
 *   4. Trial ending soon (≤ 7 days) — amber warning
 *
 * Each banner is individually dismissable within the current session.
 */

const BANNER_CONFIGS = {
  subscriptionExpiring: {
    id: "billing-alert-sub-expiring",
    icon: AlertTriangle,
    bgClass: "bg-amber-50 border-amber-200",
    iconClass: "text-amber-600",
    textClass: "text-amber-900",
    ctaBgClass: "bg-amber-600 hover:bg-amber-700 text-white",
    getMessage: (days) => `Your subscription expires in ${days} day${days !== 1 ? "s" : ""}.`,
    ctaLabel: "Extend Subscription",
    ctaHref: "/settings/subscription",
    ctaExternal: false,
  },
  subscriptionExpired: {
    id: "billing-alert-sub-expired",
    icon: XCircle,
    bgClass: "bg-red-50 border-red-200",
    iconClass: "text-red-600",
    textClass: "text-red-900",
    ctaBgClass: "bg-red-600 hover:bg-red-700 text-white",
    getMessage: () => "Your subscription has expired.",
    ctaLabel: "Upgrade to Monthly/Annual",
    ctaHref: "/settings/subscription",
    ctaExternal: false,
  },
  paymentFailed: {
    id: "billing-alert-payment-failed",
    icon: CreditCard,
    bgClass: "bg-red-50 border-red-200",
    iconClass: "text-red-600",
    textClass: "text-red-900",
    ctaBgClass: "bg-red-600 hover:bg-red-700 text-white",
    getMessage: () => "Your last payment failed. Please update your payment method.",
    ctaLabel: "Update Payment Method",
    ctaHref: "/settings/payment-billing",
    ctaExternal: false,
  },
  trialEndingSoon: {
    id: "billing-alert-trial-ending",
    icon: AlertTriangle,
    bgClass: "bg-amber-50 border-amber-200",
    iconClass: "text-amber-600",
    textClass: "text-amber-900",
    ctaBgClass: "bg-amber-600 hover:bg-amber-700 text-white",
    getMessage: (days) => `Your free trial ends in ${days} day${days !== 1 ? "s" : ""}.`,
    ctaLabel: "Upgrade Now",
    ctaHref: "/settings/subscription",
    ctaExternal: false,
  },
};

function BannerItem({ config, message, onDismiss }) {
  const Icon = config.icon;

  return (
    <div
      className={`
        flex items-center gap-3 rounded-lg border px-4 py-3
        shadow-sm transition-all duration-300 animate-in slide-in-from-top-2 fade-in
        ${config.bgClass}
      `}
      role="alert"
    >
      <Icon className={`h-5 w-5 flex-shrink-0 ${config.iconClass}`} />

      <p className={`flex-1 text-sm font-medium ${config.textClass}`}>
        {message}
      </p>

      {/* CTA Button */}
      {config.ctaExternal ? (
        <a
          href={process.env.REACT_APP_CLINIC_ONBOARDING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`
            inline-flex items-center gap-1.5 rounded-md px-3 py-1.5
            text-xs font-semibold shadow-sm transition-colors flex-shrink-0
            ${config.ctaBgClass}
          `}
        >
          {config.ctaLabel}
          <ArrowUpRight className="h-3 w-3" />
        </a>
      ) : (
        <Link
          href={config.ctaHref}
          className={`
            inline-flex items-center gap-1.5 rounded-md px-3 py-1.5
            text-xs font-semibold shadow-sm transition-colors flex-shrink-0
            ${config.ctaBgClass}
          `}
        >
          {config.ctaLabel}
          <RefreshCw className="h-3 w-3" />
        </Link>
      )}

      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-white/60 transition-colors flex-shrink-0"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function BillingAlertBanner() {
  const subscription = useSubscription();
  const [location] = useLocation();

  // Dismissed state keyed by banner id — stored in sessionStorage so it
  // resets on next session but persists within the current one.
  const [dismissed, setDismissed] = useState(() => {
    try {
      const stored = sessionStorage.getItem("dismissedBillingAlerts");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const dismiss = (bannerId) => {
    setDismissed((prev) => {
      const next = { ...prev, [bannerId]: true };
      try {
        sessionStorage.setItem("dismissedBillingAlerts", JSON.stringify(next));
      } catch { /* ignore */ }
      return next;
    });
  };

  // Determine which alerts are active
  const activeBanners = useMemo(() => {
    const banners = [];

    const {
      isTrial,
      trialDaysLeft,
      subscriptionDaysLeft,
      paymentFailed,
    } = subscription;

    // Payment failure — highest priority
    if (paymentFailed) {
      banners.push({
        config: BANNER_CONFIGS.paymentFailed,
        message: BANNER_CONFIGS.paymentFailed.getMessage(),
      });
    }

    // Subscription expired
    if (subscriptionDaysLeft <= 0) {
      banners.push({
        config: BANNER_CONFIGS.subscriptionExpired,
        message: BANNER_CONFIGS.subscriptionExpired.getMessage(),
      });
    }
    // Subscription expiring soon (within 7 days)
    else if (subscriptionDaysLeft <= 7) {
      banners.push({
        config: BANNER_CONFIGS.subscriptionExpiring,
        message: BANNER_CONFIGS.subscriptionExpiring.getMessage(subscriptionDaysLeft),
      });
    }

    // Trial ending soon (within 7 days, and trial is still active)
    if (isTrial && trialDaysLeft > 0 && trialDaysLeft <= 7) {
      banners.push({
        config: BANNER_CONFIGS.trialEndingSoon,
        message: BANNER_CONFIGS.trialEndingSoon.getMessage(trialDaysLeft),
      });
    }

    return banners;
  }, [subscription]);

  // Filter out dismissed banners
  const visibleBanners = activeBanners.filter(
    (banner) => !dismissed[banner.config.id]
  );

  const isDashboard = location === "/";
  const isSettings = location.startsWith("/settings") || location === "/profile-settings";

  if (!isDashboard && !isSettings) return null;
  const isHidden = true; // Hidden unconditionally for Dev and Test
  if (isHidden) return null;
  if (visibleBanners.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      {visibleBanners.map((banner) => (
        <BannerItem
          key={banner.config.id}
          config={banner.config}
          message={banner.message}
          onDismiss={() => dismiss(banner.config.id)}
        />
      ))}
    </div>
  );
}

